import { type ProjectProgress, useContentProgress, getProjectProgressStatus } from 'ContentProgress';
import Button from 'Button';
import { isNullPeriodAfterEndDate, isPeriodStarted } from 'Cohorts';
import { formattedUserFacingMonthDayYearLong } from 'DateHelpers';
import { useCurrentUser } from 'FrontRoyalAngular';
import { MailtoLink } from 'MailtoLink';
import { type TFunction } from 'i18next';
import { type ProjectGroupMembershipSansProjectGroup } from 'ProjectGroup';
import { ProjectFeedbackModal } from 'ProjectFeedback';
import { getProgramConfigValue } from 'Program';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons/faExclamationCircle';
import { Divider } from '@heroui/react';
import { type FC, type JSX, memo, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Text } from 'Text';
import { getCohort } from 'Users';
import { type LearnerProject } from './LearnerProjects.types';
import { ProjectLinks } from './ProjectLinks';
import { ProjectSubmissionAttachmentsList } from './ProjectSubmissionAttachmentsList';
import { getProjectDates } from './learnerProjects';
import { useProjectModalHandlers } from './ProjectModalContext';
import { usePeriodIndexForProjectUnlock } from './useProjectsForPlaylists';

const CreateProjectGroupButtonPresentationComponent: FC = () => {
    const { openProjectGroupSetupModal } = useProjectModalHandlers();
    const { t } = useTranslation('back_royal', { keyPrefix: 'learnerProjects.learnerProjects' });

    return (
        <>
            <Divider className="-mx-5 w-auto" />
            <Text className="text-base text-slate-grey">
                <Trans
                    i18nKey="createProjectGroupDescription"
                    t={t}
                    components={{
                        strong: <span className="font-semibold" />,
                    }}
                />
            </Text>
            <Button variant="bordered" color="slate-grey" fullWidth height={9} onPress={openProjectGroupSetupModal}>
                {t('createProjectGroup')}
            </Button>
            <Divider className="-mx-5 w-auto" />
        </>
    );
};
const CreateProjectGroupButtonPresentation = memo(CreateProjectGroupButtonPresentationComponent);

const ViewProjectGroupButtonPresentationComponent: FC<{
    projectGroupMembership: ProjectGroupMembershipSansProjectGroup;
}> = ({ projectGroupMembership }) => {
    const { openViewProjectGroupModal } = useProjectModalHandlers();
    const { t } = useTranslation('back_royal', { keyPrefix: 'learnerProjects.learnerProjects' });

    return (
        <Button
            variant="bordered"
            color="slate-grey"
            fullWidth
            height={9}
            onPress={() => openViewProjectGroupModal(projectGroupMembership)}
        >
            {t('viewProjectGroup')}
        </Button>
    );
};
const ViewProjectGroupButtonPresentation = memo(ViewProjectGroupButtonPresentationComponent);

const SubmitProjectButtonPresentationComponent: FC<{
    projectGroupMembership?: ProjectGroupMembershipSansProjectGroup;
    submissionButtonTranslationKey?: string;
}> = ({ projectGroupMembership, submissionButtonTranslationKey = 'submitProject' }) => {
    const { openProjectSubmissionModal } = useProjectModalHandlers();
    const { t } = useTranslation('back_royal', { keyPrefix: 'learnerProjects.learnerProjects' });

    const onPress = useCallback(() => {
        openProjectSubmissionModal(projectGroupMembership);
    }, [openProjectSubmissionModal, projectGroupMembership]);

    return (
        <Button color="black" fullWidth height={9} onPress={onPress}>
            {t(submissionButtonTranslationKey)}
        </Button>
    );
};
const SubmitProjectButtonPresentation = memo(SubmitProjectButtonPresentationComponent);

const ProjectSubmissionActionsComponent: FC<{
    acceptedProjectGroupMembership?: ProjectGroupMembershipSansProjectGroup;
    project: LearnerProject;
    // Whether or not to render the create/view Project Group buttons. Note that hiding the buttons in this component does not prevent ProjectSubmissionFormModal
    // from initiating either of create/view Project Group modals from its UI.
    showProjectGroupActions?: boolean;
    submissionButtonTranslationKey?: string;
}> = ({
    acceptedProjectGroupMembership,
    project,
    showProjectGroupActions = true,
    submissionButtonTranslationKey = 'submitProject',
}) => (
    <>
        {showProjectGroupActions && project.groupProject && !acceptedProjectGroupMembership && (
            <CreateProjectGroupButtonPresentation />
        )}
        {showProjectGroupActions && acceptedProjectGroupMembership && (
            <ViewProjectGroupButtonPresentation projectGroupMembership={acceptedProjectGroupMembership} />
        )}
        <SubmitProjectButtonPresentation
            projectGroupMembership={acceptedProjectGroupMembership}
            submissionButtonTranslationKey={submissionButtonTranslationKey}
        />
    </>
);
const ProjectSubmissionActions = memo(ProjectSubmissionActionsComponent);

// We only call this when the progress is passed or failed, meaning we have a score set (although we don't necessarily have feedback).
const ViewProjectFeedbackComponent: FC<{
    progress: ProjectProgress;
    project: LearnerProject;
    failed: boolean;
    passed: boolean;
    solidButton?: boolean;
    acceptedProjectGroupMembership?: ProjectGroupMembershipSansProjectGroup;
    resubmissionRequired?: boolean;
}> = ({
    progress,
    project,
    failed,
    passed,
    solidButton = true,
    acceptedProjectGroupMembership,
    resubmissionRequired,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'learnerProjects.learnerProjects' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const onPress = useCallback(async () => {
        setIsModalOpen(true);
    }, [setIsModalOpen]);

    return (
        <>
            {solidButton ? (
                <Button color="black" fullWidth height={9} onPress={onPress}>
                    {t('viewProjectFeedback')}
                </Button>
            ) : (
                <Button color="slate-grey" fullWidth height={9} onPress={onPress} variant="bordered">
                    {t('viewProjectFeedback')}
                </Button>
            )}
            <ProjectFeedbackModal
                closeModal={() => setIsModalOpen(false)}
                isOpen={isModalOpen}
                progress={progress}
                project={project}
                failed={failed}
                passed={passed}
                acceptedProjectGroupMembership={acceptedProjectGroupMembership}
                resubmissionRequired={resubmissionRequired}
            />
        </>
    );
};
const ViewProjectFeedback = memo(ViewProjectFeedbackComponent);

const QuestionsComponent: FC<{ projectEmail: string }> = ({ projectEmail }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'learnerProjects.learnerProjects' });
    return (
        <Text as="small" className="text-center">
            <Trans
                i18nKey="questions"
                t={t}
                components={{ projectEmail: <MailtoLink className="text-sm" email={projectEmail} /> }}
            />
        </Text>
    );
};
const Questions = memo(QuestionsComponent);

const NotStartedProjectContentComponent: FC<{
    acceptedProjectGroupMembership?: ProjectGroupMembershipSansProjectGroup;
    project: LearnerProject;
    projectEmail: string;
}> = ({ acceptedProjectGroupMembership, project, projectEmail }) => (
    <>
        <ProjectLinks project={project} locked={false} />
        <ProjectSubmissionActions acceptedProjectGroupMembership={acceptedProjectGroupMembership} project={project} />
        <Questions projectEmail={projectEmail} />
    </>
);
const NotStartedProjectContent = memo(NotStartedProjectContentComponent);

const ResubmissionRequiredProjectContentComponent: FC<{
    progress: ProjectProgress;
    project: LearnerProject;
    acceptedProjectGroupMembership?: ProjectGroupMembershipSansProjectGroup;
    projectEmail: string;
}> = ({ progress, project, acceptedProjectGroupMembership, projectEmail }) => {
    const hasFeedback = !!progress.feedback;

    return (
        <>
            <ProjectLinks project={project} locked={false} />
            <div className="flex flex-col gap-2">
                {hasFeedback && (
                    <ViewProjectFeedback
                        project={project}
                        progress={progress}
                        failed={false}
                        passed={false}
                        solidButton={false}
                        acceptedProjectGroupMembership={acceptedProjectGroupMembership}
                        resubmissionRequired
                    />
                )}
                <ProjectSubmissionActions
                    acceptedProjectGroupMembership={acceptedProjectGroupMembership}
                    project={project}
                    showProjectGroupActions={false}
                    submissionButtonTranslationKey="resubmitProject"
                />
            </div>
            <Questions projectEmail={projectEmail} />
        </>
    );
};
const ResubmissionRequiredProjectContent = memo(ResubmissionRequiredProjectContentComponent);

const CompletedProjectContentComponent: FC<{
    progress: ProjectProgress;
    project: LearnerProject;
    acceptedProjectGroupMembership?: ProjectGroupMembershipSansProjectGroup;
    failed: boolean;
    passed: boolean;
    projectEmail: string;
}> = ({ progress, project, acceptedProjectGroupMembership, failed, passed, projectEmail }) => {
    const hasScoreAndFeedback = typeof progress.score === 'number' && progress.feedback;

    return (
        <>
            {(hasScoreAndFeedback || progress.allowResubmission) && (
                <>
                    <ProjectLinks project={project} locked={false} />
                    <div className="flex flex-col gap-2">
                        {hasScoreAndFeedback && (
                            <ViewProjectFeedback
                                project={project}
                                progress={progress}
                                failed={failed}
                                passed={passed}
                                solidButton={!progress.allowResubmission}
                                acceptedProjectGroupMembership={acceptedProjectGroupMembership}
                            />
                        )}
                        {progress.allowResubmission && (
                            <ProjectSubmissionActions
                                acceptedProjectGroupMembership={acceptedProjectGroupMembership}
                                project={project}
                                showProjectGroupActions={false}
                                submissionButtonTranslationKey="resubmitProject"
                            />
                        )}
                    </div>
                </>
            )}
            {failed && <Questions projectEmail={projectEmail} />}
        </>
    );
};
const CompletedProjectContent = memo(CompletedProjectContentComponent);

type ProjectStatusParams = {
    currentUserId: string;
    progress?: ProjectProgress;
    titleSize?: 'base' | 'xs' | 'sm';
    project: LearnerProject;
    acceptedProjectGroupMembership?: ProjectGroupMembershipSansProjectGroup;
    failed: boolean;
    formattedEndDate: string;
    formattedStartDate: string;
    locked: boolean;
    passed: boolean;
    projectEmail: string;
    status: string;
    t: TFunction;
};

// oxlint-disable-next-line max-lines-per-function, complexity
export const getProjectStatusConfig = ({
    acceptedProjectGroupMembership,
    currentUserId,
    titleSize,
    failed,
    formattedEndDate,
    formattedStartDate,
    locked,
    passed,
    progress,
    project,
    projectEmail,
    status,
    t,
}: ProjectStatusParams): {
    text: string | React.ReactNode;
    content?: React.ReactNode;
    icon?: JSX.Element;
} => {
    const submission = progress?.projectSubmission;
    const isInProjectGroup = !!acceptedProjectGroupMembership;
    const submittedByOther = isInProjectGroup && submission?.userId && submission.userId !== currentUserId;
    const submittedForGroup = isInProjectGroup && submission?.userId && submission.userId === currentUserId;
    const submittedI18nKey = submittedByOther
        ? 'submittedByOther'
        : submittedForGroup
          ? 'submittedForGroup'
          : 'submitted';
    const attachments = submission?.projectSubmissionAttachments ?? [];
    // An admin can allow a resubmission before the grade is approved. The record then has no
    // score, so the status is notStarted. The admin-supplied due date must win over the
    // project end date there.
    const formattedResubmissionDueDate =
        progress?.allowResubmission && progress.resubmissionDueDate
            ? formattedUserFacingMonthDayYearLong(progress.resubmissionDueDate * 1000)
            : null;

    const states = {
        inProgress: {
            text: (
                <Trans
                    i18nKey={submittedI18nKey}
                    t={t}
                    components={{
                        strong: <span className="font-semibold" />,
                    }}
                    values={{
                        projectTitle: project.title,
                        submittedByName: submission?.userName,
                    }}
                />
            ),
            content:
                attachments.length > 0 ? <ProjectSubmissionAttachmentsList attachments={attachments} /> : undefined,
        },
        completed: {
            passed: {
                text: (
                    <Trans
                        i18nKey="passed"
                        t={t}
                        components={{
                            strong: <span className="font-bold" />,
                            med: <span className="font-semibold" />,
                        }}
                        values={{
                            score: progress?.score,
                            maxScore: 5,
                        }}
                    />
                ),
                content: (
                    <CompletedProjectContent
                        progress={progress!}
                        project={project}
                        acceptedProjectGroupMembership={acceptedProjectGroupMembership}
                        failed={false}
                        passed
                        projectEmail={projectEmail}
                    />
                ),
                icon: <FontAwesomeIcon icon={faCheck} className="text-coral" />,
            },
            failed: {
                text: (
                    <Trans
                        i18nKey="failed"
                        t={t}
                        components={{
                            strong: <span className="font-bold" />,
                            resubmitByDate:
                                progress && progress.allowResubmission && progress.resubmissionDueDate ? (
                                    <span className="font-regular" />
                                ) : (
                                    <span style={{ display: 'none' }} />
                                ),
                        }}
                        values={{
                            dueDate: progress?.resubmissionDueDate
                                ? formattedUserFacingMonthDayYearLong(progress.resubmissionDueDate * 1000)
                                : '',
                            maxScore: 5,
                            score: progress?.score,
                        }}
                    />
                ),
                content: (
                    <CompletedProjectContent
                        progress={progress!}
                        project={project}
                        acceptedProjectGroupMembership={acceptedProjectGroupMembership}
                        failed
                        passed={false}
                        projectEmail={projectEmail}
                    />
                ),
                icon: <FontAwesomeIcon icon={faExclamationCircle} className="text-coral" />,
            },
            resubmissionRequired: {
                text: (
                    <Trans
                        i18nKey="resubmissionRequired"
                        t={t}
                        components={{
                            strong: <span className="font-bold" />,
                            resubmitByDate:
                                progress && progress.resubmissionDueDate ? (
                                    <span className="font-regular" />
                                ) : (
                                    <span style={{ display: 'none' }} />
                                ),
                        }}
                        values={{
                            dueDate: progress?.resubmissionDueDate
                                ? formattedUserFacingMonthDayYearLong(progress.resubmissionDueDate * 1000)
                                : '',
                        }}
                    />
                ),
                content: (
                    <ResubmissionRequiredProjectContent
                        progress={progress!}
                        project={project}
                        acceptedProjectGroupMembership={acceptedProjectGroupMembership}
                        projectEmail={projectEmail}
                    />
                ),
                icon: <FontAwesomeIcon icon={faExclamationCircle} className="text-coral" />,
            },
            waived: {
                text: (
                    <Trans
                        i18nKey="waived"
                        t={t}
                        components={{
                            strong: <span className="font-bold" />,
                            med: <span className="font-semibold" />,
                        }}
                    />
                ),
                icon: <FontAwesomeIcon icon={faCheck} className="text-coral" />,
            },
        },
        locked: {
            text: (
                <Trans
                    i18nKey="notStartedLocked"
                    t={t}
                    values={{
                        projectTitle: project.title,
                        dueDate: formattedEndDate,
                        unlockDate: formattedStartDate,
                    }}
                />
            ),
            content: <ProjectLinks project={project} locked={locked} titleSize={titleSize} />,
        },
        notStarted: {
            text: (
                <Trans
                    i18nKey="notStarted"
                    t={t}
                    values={{
                        projectTitle: project.title,
                        dueDate: formattedResubmissionDueDate ?? formattedEndDate,
                    }}
                />
            ),
            content: (
                <NotStartedProjectContent
                    project={project}
                    acceptedProjectGroupMembership={acceptedProjectGroupMembership}
                    projectEmail={projectEmail}
                />
            ),
        },
    };

    // Determine which state to return
    if (status === 'inProgress') return states.inProgress;

    if (status === 'completed') {
        if (progress!.waived) return states.completed.waived;
        if (progress!.resubmissionRequired) return states.completed.resubmissionRequired;
        if (passed) return states.completed.passed;
        if (failed) return states.completed.failed;
    }

    if (locked) return states.locked;

    return states.notStarted;
};

export const useProjectStatusConfig = (project: LearnerProject, titleSize?: 'base' | 'xs' | 'sm') => {
    const currentUser = useCurrentUser();
    const cohort = getCohort(currentUser);
    const { t } = useTranslation('back_royal', { keyPrefix: 'learnerProjects.learnerProjects' });
    const { projectProgressByRequirementIdentifier } = useContentProgress();
    const projectPeriodIndex = usePeriodIndexForProjectUnlock(project.requirementIdentifier);
    const { startDate, endDate } = getProjectDates(cohort, project, projectPeriodIndex);
    const locked = !isPeriodStarted(cohort, projectPeriodIndex) && !isNullPeriodAfterEndDate(cohort);
    const progress = projectProgressByRequirementIdentifier[project.requirementIdentifier];

    const status = getProjectProgressStatus(progress);
    const failed = progress?.passed === false;
    const passed = progress?.passed === true;

    const formattedStartDate = useMemo(
        () => (startDate ? formattedUserFacingMonthDayYearLong(startDate, false) : ''),
        [startDate],
    );
    const formattedEndDate = useMemo(() => (endDate ? formattedUserFacingMonthDayYearLong(endDate) : ''), [endDate]);
    const projectEmail = ((cohort && getProgramConfigValue(cohort.programType, 'projectEmail')) ?? '') as string;

    const acceptedProjectGroupMembership = useMemo(
        () =>
            currentUser?.acceptedProjectGroupMemberships.find(
                membership => membership.requirementIdentifier === project.requirementIdentifier,
            ),
        [currentUser?.acceptedProjectGroupMemberships, project],
    );

    return useMemo(
        () =>
            getProjectStatusConfig({
                currentUserId: currentUser!.id,
                progress,
                titleSize,
                project,
                acceptedProjectGroupMembership,
                failed,
                formattedEndDate,
                formattedStartDate,
                locked,
                passed,
                projectEmail,
                status,
                t,
            }),
        [
            currentUser,
            progress,
            titleSize,
            project,
            acceptedProjectGroupMembership,
            failed,
            formattedEndDate,
            formattedStartDate,
            locked,
            passed,
            projectEmail,
            status,
            t,
        ],
    );
};
